import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const PaymentForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get plan type from URL params or location state
  const planType = new URLSearchParams(location.search).get('plan') || location.state?.planType || 'core';

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_API_URL}/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          plan_type: planType
        })
      });

      // Check if response is ok first
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Debug: Log response details
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      let data;
      try {
        data = await response.json();
        console.log('Parsed response data:', data);
      } catch (jsonError) {
        console.error('JSON parse error:', jsonError);
        throw new Error('Invalid response format from server');
      }

      // Check if data exists and has success property
      if (data && data.success) {
        // Redirect to payment page or show payment details
        navigate('/payment-details', { 
          state: { 
            paymentData: data,
            planType: planType
          }
        });
      } else {
        // Handle case where data exists but success is false
        const errorMessage = data?.detail || data?.message || 'Failed to create invoice';
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Payment form error:', err);
      if (err.message.includes('HTTP error')) {
        setError(`Server error: ${err.message}`);
      } else if (err.message.includes('Invalid response format')) {
        setError('Server returned invalid response format');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getPlanDetails = () => {
    const plans = {
      core: { name: 'Core Plan', price: 97, color: '#10B981', emoji: '🟢' },
      growth: { name: 'Growth Plan', price: 497, color: '#3B82F6', emoji: '🔵' },
      enterprise: { name: 'Enterprise Plan', price: 997, color: '#8B5CF6', emoji: '🟣' },
      ultimate: { name: 'Ultimate Plan', price: 2997, color: '#F59E0B', emoji: '⭐' },
      ultamega: { name: 'Ulta Mega Plan', price: 4997, color: '#EF4444', emoji: '🔥' }
    };
    return plans[planType] || plans.core;
  };

  const planDetails = getPlanDetails();

  return (
    <Container>
      <PaymentCard>
        <Header>
          <PlanEmoji>{planDetails.emoji}</PlanEmoji>
          <PlanTitle>{planDetails.name}</PlanTitle>
          <PlanPrice>${planDetails.price}/month</PlanPrice>
        </Header>

        <FormSection>
          <FormTitle>Complete Your Purchase</FormTitle>
          <FormSubtitle>
            Enter your details to create a USDT payment invoice. You'll receive a unique payment address to complete your subscription.
          </FormSubtitle>

          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="username">Username *</Label>
              <Input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="Enter your desired username"
                required
              />
              <HelpText>This will be your unique identifier for API access</HelpText>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                required
              />
              <HelpText>We'll send payment confirmation and API credentials here</HelpText>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Creating Invoice...' : 'Create USDT Invoice'}
            </SubmitButton>
          </Form>
        </FormSection>

        <InfoSection>
          <InfoTitle>🔐 Secure USDT Payment</InfoTitle>
          <InfoList>
            <InfoItem>• Payment processed via Coinremitter (trusted crypto payment processor)</InfoItem>
            <InfoItem>• 24-hour payment window</InfoItem>
            <InfoItem>• Automatic subscription activation upon confirmation</InfoItem>
            <InfoItem>• No personal financial data stored</InfoItem>
            <InfoItem>• Instant API access after payment confirmation</InfoItem>
            <InfoItem>• Note: If USDT is unavailable, we may fall back to BTC</InfoItem>
          </InfoList>
        </InfoSection>
      </PaymentCard>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: #F9FAFB;
  padding: 2rem 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaymentCard = styled.div`
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  text-align: center;
`;

const PlanEmoji = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const PlanTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 800;
`;

const FormSection = styled.div`
  padding: 2rem;
`;

const FormTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const FormSubtitle = styled.p`
  color: #6B7280;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #9CA3AF;
  }
`;

const HelpText = styled.p`
  font-size: 0.75rem;
  color: #6B7280;
  margin: 0;
`;

const ErrorMessage = styled.div`
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #DC2626;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
`;

const SubmitButton = styled.button`
  background: #000000;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #333333;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #9CA3AF;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoSection = styled.div`
  background: #F9FAFB;
  padding: 1.5rem 2rem;
  border-top: 1px solid #E5E7EB;
`;

const InfoTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoItem = styled.li`
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.4;
`;

export default PaymentForm; 